import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Footer from '../../layout/footer/footer';
import '../../styles/category.css';
import { getPosts } from '../../services/postService';
import { imgBaseUrl } from '../../utils/api';

const extractCategoryId = (value) => {
  if (!value && value !== 0) {
    return null;
  }
  const match = String(value).match(/(\d+)$/);
  const candidate = match ? Number(match[1]) : Number(value);
  return Number.isFinite(candidate) ? candidate : null;
};

const normalizeKey = (value) => String(value ?? '').trim().toLowerCase();

const slugify = (value) => normalizeKey(value)
  .replace(/[^a-z0-9\u0600-\u06ff]+/g, '-')
  .replace(/-+/g, '-')
  .replace(/(^-|-$)/g, '');

const buildImageUrl = (imageName) => {
  if (!imageName) {
    return null;
  }
  if (/^https?:/i.test(imageName)) {
    return imageName;
  }
  const base = (imgBaseUrl || '').replace(/\/$/, '');
  const normalized = String(imageName).replace(/^\//, '');
  return base ? `${base}/${normalized}` : normalized;
};

const sanitizeExcerpt = (value) => {
  if (!value) {
    return '';
  }
  const withoutHtml = String(value).replace(/<[^>]+>/g, '');
  return withoutHtml.length > 160 ? `${withoutHtml.slice(0, 157).trim()}...` : withoutHtml;
};

const CategoryStatus = ({ message }) => (
  <div className="categorypp-empty" role="status">
    {message}
  </div>
);

const CategoryCard = ({ post }) => {
  const navigate = useNavigate();
  const primaryCategory = post.categoryList?.[0]?.title || 'بدون دسته‌بندی';
  const imageUrl = buildImageUrl(post.imageName || post.smallImageName);
  const excerpt = sanitizeExcerpt(post.shortDescription || post.headline || '');
  const visitCount = typeof post.visitCount === 'number' ? post.visitCount : 0;
  const publishDate = post.shamsiCreateDate || '';
  const writer = post.writer?.trim() || 'خبرنگار نامشخص';
  const writerInitial = writer.charAt(0) || 'ن';
  const detailsPath = post.pageId ? `/post/${post.pageId}` : '#';

  const handleNavigate = (event) => {
    if (event.defaultPrevented || !post.pageId) {
      return;
    }
    navigate(detailsPath);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleNavigate(event);
    }
  };

  return (
    <article
      className="categorypp-card"
      role="link"
      tabIndex={0}
      onClick={handleNavigate}
      onKeyDown={handleKeyDown}
    >
      {imageUrl ? (
        <Link to={detailsPath} aria-label={`مشاهده خبر ${post.title || primaryCategory}`} className="categorypp-card-imglink">
          <img className="categorypp-card-img" src={imageUrl} alt={post.title || primaryCategory} loading="lazy" />
        </Link>
      ) : (
        <div className="categorypp-card-img categorypp-card-img--placeholder" aria-hidden="true" />
      )}
      <div className="categorypp-card-body">
        <h3 className="categorypp-card-title">
          <Link to={detailsPath}>{post.title || 'بدون عنوان'}</Link>
        </h3>
        {excerpt && <p className="categorypp-card-excerpt">{excerpt}</p>}
      </div>
      <div className="categorypp-card-footer dd" style={{fontSize: '12px !important'}}>
        <div className="categorypp-readtime">
          بازدید <strong>{visitCount}</strong>
          {publishDate ? ` · ${publishDate}` : ''}
        </div>
        <div className="categorypp-author d-none">
          <div className="categorypp-avatar categorypp-avatar--placeholder" aria-hidden="true">
            {writerInitial}
          </div>
          <span>{writer}</span>
        </div>
      </div>
    </article>
  );
};

const CategoryPostsGrid = ({ items, loading, error }) => {
  if (loading) {
    return <CategoryStatus message="در حال بارگذاری اخبار..." />;
  }

  if (error) {
    return <CategoryStatus message={error} />;
  }

  if (!items.length) {
    return <CategoryStatus message="خبری برای این دسته پیدا نشد." />;
  }

  return items.map((post) => <CategoryCard key={post.pageId ?? post.title} post={post} />);
};

// New Pagination Component
// Fixed Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange, isLoading }) => {
  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(0, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages - 1, start + maxVisible - 1);

    if (end - start < maxVisible - 1) {
      start = Math.max(0, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return { pages, start, end };
  };

  const { pages, start, end } = getPageNumbers();

  const handlePageChange = (page) => {
    if (page !== currentPage && !isLoading) {
      onPageChange(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <nav className="categorypp-pagination" aria-label="صفحه‌بندی">
      <ul className="categorypp-pagination-list">
        <li className="categorypp-pagination-item">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0 || isLoading}
            className="categorypp-pagination-btn categorypp-pagination-prev"
            aria-label="صفحه قبلی"
          >
            <i className="fa-solid fa-chevron-left" aria-hidden="true"></i>
          </button>
        </li>

        {start > 0 && (
          <>
            <li className="categorypp-pagination-item">
              <button
                onClick={() => handlePageChange(0)}
                className="categorypp-pagination-btn"
                disabled={isLoading}
              >
                ۱
              </button>
            </li>
            {start > 1 && (
              <li className="categorypp-pagination-item categorypp-pagination-ellipsis">...</li>
            )}
          </>
        )}

        {pages.map((page) => (
          <li key={page} className="categorypp-pagination-item">
            <button
              onClick={() => handlePageChange(page)}
              className={`categorypp-pagination-btn ${page === currentPage ? 'is-active' : ''}`}
              disabled={isLoading}
              aria-current={page === currentPage ? 'page' : undefined}
            >
              {page + 1}
            </button>
          </li>
        ))}

        {end < totalPages - 1 && (
          <>
            {end < totalPages - 2 && (
              <li className="categorypp-pagination-item categorypp-pagination-ellipsis">...</li>
            )}
            <li className="categorypp-pagination-item">
              <button
                onClick={() => handlePageChange(totalPages - 1)}
                className="categorypp-pagination-btn"
                disabled={isLoading}
              >
                {totalPages}
              </button>
            </li>
          </>
        )}

        <li className="categorypp-pagination-item">
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages - 1 || isLoading}
            className="categorypp-pagination-btn categorypp-pagination-next"
            aria-label="صفحه بعدی"
          >
            <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
          </button>
        </li>
      </ul>
    </nav>
  );
};

const CategorySidebar = ({ categories, currentId, isLoading, error }) => {
  const [term, setTerm] = useState('');
  const normalizedTerm = useMemo(() => normalizeKey(term), [term]);

  const filtered = useMemo(() => {
    if (!Array.isArray(categories)) {
      return [];
    }
    if (!normalizedTerm) {
      return categories;
    }
    return categories.filter((category) => normalizeKey(category.title).includes(normalizedTerm));
  }, [categories, normalizedTerm]);

  return (
    <>
      <div className="categorypp-search">
        <input
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          placeholder="جستجو در دسته‌ها..."
          aria-label="جستجو در دسته‌بندی‌ها"
        />
        <i className="fa-solid fa-magnifying-glass categorypp-search-icon" aria-hidden="true" />
      </div>

      <div className="categorypp-side-title">دسته‌بندی‌ها</div>

      {isLoading && <div className="categorypp-sidebar-status">در حال بارگذاری...</div>}
      {error && !isLoading && <div className="categorypp-sidebar-status">{error}</div>}

      {!isLoading && !error && (
        <ul className="categorypp-catlist">
          {filtered.map((category) => {
            const isActive = category.id === currentId;
            return (
              <li key={category.id}>
                <Link
                  to={`/news/${category.id}`}
                  className={isActive ? 'is-active' : undefined}
                >
                  {category.title}
                </Link>
              </li>
            );
          })}
          {!filtered.length && <li className="categorypp-sidebar-status">نتیجه‌ای یافت نشد.</li>}
        </ul>
      )}
    </>
  );
};

const CategoryPage = () => {
  const { categoryKey = '' } = useParams();
  const { categoryId, normalizedKey } = useMemo(() => ({
    categoryId: extractCategoryId(categoryKey),
    normalizedKey: normalizeKey(categoryKey),
  }), [categoryKey]);

  const [categoriesState, setCategoriesState] = useState({ items: [], loading: true, error: '' });
  const [postsState, setPostsState] = useState({ items: [], loading: true, error: '' });
  const [categoryTitle, setCategoryTitle] = useState('');
  const [categoryError, setCategoryError] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    setCategoryTitle('');
    setCategoryError('');
    setCurrentPage(0); // Reset to first page when category changes
    setTotalPages(0);
    setTotalItems(0);
  }, [categoryId, normalizedKey]);

  // Load posts with pagination
  useEffect(() => {
    let active = true;
    setPostsState({ items: [], loading: true, error: '' });
    
    (async () => {
      try {
        const isNumberOnly = /^\d+$/.test(categoryKey);
        const take = 12; // Items per page
        
        let filter = {
          pageId: currentPage,
          take: take,
          selectedGroup: [],
          text: ''
        };

        if (isNumberOnly && categoryId) {
          filter.selectedGroup = [categoryId];
        } else if (!isNumberOnly && categoryKey) {
          filter.text = categoryKey;
        }
        
        const response = await getPosts(filter);
        
        if (!active) return;
        
        // Handle response format - could be array or object with posts property
        let posts = [];
        let total = 0;
        
        if (Array.isArray(response)) {
          posts = response;
          total = response.length > 0 && response[0]?.total ? response[0].total : response.length;
        } else if (response?.posts) {
          posts = response.posts;
          total = posts[0].total || response.total || posts.length;
        } else {
          posts = response || [];
          total = posts.length;
        }

        // Calculate total pages
        const calculatedTotalPages = Math.ceil(total / take);
        setTotalPages(calculatedTotalPages);
        setTotalItems(total);
        
        setPostsState({ items: posts, loading: false, error: '' });
        
        // Set category title from posts
        if (categoryId && posts.length) {
          const fromPosts = posts
            .flatMap((post) => (Array.isArray(post.categoryList) ? post.categoryList : []))
            .find((item) => item.id === categoryId) || posts[0]?.categoryList?.[0];
          
          if (fromPosts?.title) {
            setCategoryTitle(fromPosts.title.trim());
          }
        }
      } catch (err) {
        if (!active) return;
        console.error('Failed to load category posts', err);
        setPostsState({ items: [], loading: false, error: 'بارگذاری اخبار با مشکل مواجه شد.' });
        setTotalPages(0);
      }
    })();
    
    return () => { active = false; };
  }, [categoryId, categoryKey, currentPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
    }
  };

  return (
    <>
      <div className="categorypp-container" dir="rtl">
        {categoryTitle && (
          <div className="categorypp-headrow">
            <div className="categorypp-topline" />
            <div className="categorypp-header">
              <div className="categorypp-title-badge">
                <span className="categorypp-title-dot" />
                <span>{categoryTitle || 'دسته‌بندی'}</span>
              </div>
              {totalItems > 0 && (
                <span className="categorypp-total-count">{totalItems} خبر</span>
              )}
            </div>
          </div>
        )}
        
        {categoryError && (
          <CategoryStatus message={categoryError} />
        )}

        <section className="categorypp-layout">
          <main>
            <div className="categorypp-cards">
              <CategoryPostsGrid
                items={postsState.items}
                loading={postsState.loading}
                error={postsState.error}
              />
            </div>
            
            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              isLoading={postsState.loading}
            />
          </main>
          {/* <aside className="categorypp-sidebar">
            <CategorySidebar
              categories={categoriesState.items}
              currentId={Number.isFinite(categoryId) ? categoryId : null}
              isLoading={categoriesState.loading}
              error={categoriesState.error}
            />
          </aside> */}
        </section>
      </div>
      <Footer />
    </>
  );
};

export default CategoryPage;